import React, { useState, useEffect, useContext } from 'react'
import { Button, Checkbox, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { styled } from '@mui/system';
import { Remove, Add } from '@mui/icons-material';
import { AuthContext } from "../auth/Auth.jsx";
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { firebaseHelper } from 'ui';
import { useCollectionDetailed } from '../global-state/useCollectionDetailed';


const FilterIconButton = styled(IconButton)(({ theme }) => ({  height:'36px', borderRadius:'10%', float:'right', cssFloat: 'right'}))


interface ProjectItem {
   id: string;
   name: string;
   count: number;
   isRefined: boolean;
   type: 'my' | 'shared';
}


interface ProjectFilterProps {
   onProjectFiltersChange: (projectIds: string[]) => void;
   onIndividualCollectionFiltersChange: (filters: { myIndividual: boolean, sharedIndividual: boolean }) => void;
   collections?: any[]; // Adding collections to trigger data refresh when they change
   isTeamsProUser?: boolean; // Teams PRO users can see all collections
   isRegularProUser?: boolean; // Regular PRO users can see individual + project collections
}


export function ProjectFilter({ onProjectFiltersChange, onIndividualCollectionFiltersChange, collections, isTeamsProUser, isRegularProUser }: ProjectFilterProps) {
   const { currentUser } = useContext(AuthContext);
   const [myProjects, setMyProjects] = useState<ProjectItem[]>([]);
   const [sharedProjects, setSharedProjects] = useState<ProjectItem[]>([]);
   const [myIndividualSelected, setMyIndividualSelected] = useState<boolean>(false);
   const [sharedIndividualSelected, setSharedIndividualSelected] = useState<boolean>(false);
   const [loading, setLoading] = useState(false);

   
   // Use the new useCollectionDetailed hook
   const {
       myIndividualCollections,
       sharedIndividualCollections,
       myProjectCollections,
       sharedProjectCollections,
       isLoading: collectionsLoading,
       initialFetchDone
   } = useCollectionDetailed();


   useEffect(() => {
       if (currentUser?.uid && initialFetchDone) {
           fetchProjectsWithCollectionCounts();
       }
   }, [currentUser?.uid, initialFetchDone, myProjectCollections.length, sharedProjectCollections.length, isTeamsProUser, isRegularProUser]);


   const fetchProjectsWithCollectionCounts = async () => {
       if (!currentUser?.uid) return;
      
       setLoading(true);
       try {
           // Fetch my projects
           const myProjectsData: Array<{id: string, name: string}> = await fetchMyProjects();
          
           // Fetch shared projects only for Teams PRO users
           const sharedProjectsData: Array<{id: string, name: string}> = isTeamsProUser ? await fetchSharedProjects() : [];
          
           // Get collection counts from useCollectionDetailed data
           const myProjectCounts = getProjectCollectionCounts(myProjectsData, myProjectCollections);
           const sharedProjectCounts = getProjectCollectionCounts(sharedProjectsData, sharedProjectCollections);
          
           // Set my projects with counts
           const myProjectsWithCounts: ProjectItem[] = myProjectsData.map(project => ({
               ...project,
               count: myProjectCounts[project.id] || 0,
               isRefined: false,
               type: 'my' as const
           }));
          
           // Set shared projects with counts
           const sharedProjectsWithCounts: ProjectItem[] = sharedProjectsData.map(project => ({
               ...project,
               count: sharedProjectCounts[project.id] || 0,
               isRefined: false,
               type: 'shared' as const
           }));
          
           setMyProjects(myProjectsWithCounts);
           setSharedProjects(sharedProjectsWithCounts);
          
       } catch (error) {
           console.error('Error fetching projects with collection counts:', error);
       } finally {
           setLoading(false);
       }
   };

   // Helper function to count collections by project from useCollectionDetailed data
   const getProjectCollectionCounts = (projects: Array<{id: string, name: string}>, collections: any[]) => {
       const counts: { [projectId: string]: number } = {};
       
       // Initialize counts
       projects.forEach(project => {
           counts[project.id] = 0;
       });
       
       // Count collections for each project
       collections.forEach(collection => {
           if (collection.associatedProjectId && counts.hasOwnProperty(collection.associatedProjectId)) {
               counts[collection.associatedProjectId]++;
           }
       });
       
       return counts;
   };


   const fetchMyProjects = async (): Promise<Array<{id: string, name: string}>> => {
       if (!currentUser?.uid) return [];
      
       try {
           const projectsCollection = collection(firebaseHelper.db, 'projectsScheme');
           const q = query(projectsCollection, where("owner", "==", currentUser.uid));
           const querySnapshot = await getDocs(q);
          
           return querySnapshot.docs.map(doc => ({
               id: doc.id,
               name: doc.data().name || 'Unnamed Project',
           }));
       } catch (error) {
           console.error('Error fetching my projects:', error);
           return [];
       }
   };


   const fetchSharedProjects = async (): Promise<Array<{id: string, name: string}>> => {
       if (!currentUser?.uid) return [];
      
       try {
           // Get user permissions
           const permissionsRef = doc(firebaseHelper.db, 'permissions', currentUser.uid);
           const permissionsSnap = await getDoc(permissionsRef);
          
           if (!permissionsSnap.exists()) {
               return [];
           }
          
           const permissions = permissionsSnap.data();
           const projectRead = permissions?.projectRead || [];
           const projectWrite = permissions?.projectWrite || [];
          
           // Combine read and write permissions, remove duplicates
           const sharedProjectIds = [...new Set([...projectRead, ...projectWrite])];
          
           if (sharedProjectIds.length === 0) {
               return [];
           }
          
           // Fetch project details for shared projects
           const projectsCollection = collection(firebaseHelper.db, 'projectsScheme');
           const sharedProjects: Array<{id: string, name: string}> = [];
          
           // Firestore 'in' query has a limit of 10 items, so we need to batch
           const batchSize = 10;
           for (let i = 0; i < sharedProjectIds.length; i += batchSize) {
               const batch = sharedProjectIds.slice(i, i + batchSize);
               const q = query(projectsCollection, where("__name__", "in", batch));
               const querySnapshot = await getDocs(q);
              
               querySnapshot.docs.forEach(doc => {
                   sharedProjects.push({
                       id: doc.id,
                       name: doc.data().name || 'Unnamed Project',
                   });
               });
           }
          
           return sharedProjects;
       } catch (error) {
           console.error('Error fetching shared projects:', error);
           return [];
       }
   };


   const handleClear = () => {
       setMyProjects(prev => prev.map(project => ({ ...project, isRefined: false })));
       setSharedProjects(prev => prev.map(project => ({ ...project, isRefined: false })));
       setMyIndividualSelected(false);
       setSharedIndividualSelected(false);
       onProjectFiltersChange([]);
       onIndividualCollectionFiltersChange({ myIndividual: false, sharedIndividual: false });
   };


   const handleIndividualFilterChange = (type: 'my' | 'shared') => {
       if (type === 'my') {
           const newValue = !myIndividualSelected;
           setMyIndividualSelected(newValue);
           onIndividualCollectionFiltersChange({
               myIndividual: newValue,
               sharedIndividual: sharedIndividualSelected
           });
       } else {
           const newValue = !sharedIndividualSelected;
           setSharedIndividualSelected(newValue);
           onIndividualCollectionFiltersChange({
               myIndividual: myIndividualSelected,
               sharedIndividual: newValue
           });
       }
   };


   const handleProjectFilterChange = (project: ProjectItem, isMyProject: boolean) => {
       const updatedProject = { ...project, isRefined: !project.isRefined };
      
       if (isMyProject) {
           const updatedMyProjects = myProjects.map(p =>
               p.id === project.id ? updatedProject : p
           );
           setMyProjects(updatedMyProjects);
       } else {
           const updatedSharedProjects = sharedProjects.map(p =>
               p.id === project.id ? updatedProject : p
           );
           setSharedProjects(updatedSharedProjects);
       }
      
       // Get all refined project IDs
       const allProjects = isMyProject
           ? [...myProjects.map(p => p.id === project.id ? updatedProject : p), ...sharedProjects]
           : [...myProjects, ...sharedProjects.map(p => p.id === project.id ? updatedProject : p)];
          
       const refinedProjectIds = allProjects
           .filter(p => p.isRefined)
           .map(p => p.id);
          
       onProjectFiltersChange(refinedProjectIds);
   };


   if (!currentUser) return null;

   // Get counts from useCollectionDetailed - these are already filtered by plan type
   const myIndividualCount = myIndividualCollections.length;
   const sharedIndividualCount = sharedIndividualCollections.length;


   return (
       <>
           <Grid item xs={12} style={{display:'flex', justifyContent:'center', height:'45px', paddingTop:'9px'}}>
               <Tooltip title="Clear Filters" arrow>
                   <FilterIconButton onClick={handleClear}>
                       <Typography style={{paddingRight:'3px'}} variant='caption'>Clear Filters</Typography>
                       <FilterAltOffIcon color="action" />
                   </FilterIconButton> 
               </Tooltip>
           </Grid>
           <Grid item xs={12}><Divider style={{paddingTop:'9px'}} /></Grid>
          
           {/* My Projects Section - Show for Regular PRO and Teams PRO users */}
           {(isRegularProUser || isTeamsProUser) && (
               <ProjectDropdown
                   attributeName='In My Projects'
                   items={myProjects}
                   onFilterChange={(project) => handleProjectFilterChange(project, true)}
                   loading={loading}
               />
           )}
          
           {/* Shared Projects Section - Show only for Teams PRO users */}
           {isTeamsProUser && (
               <ProjectDropdown
                   attributeName='In Shared Projects'
                   items={sharedProjects}
                   onFilterChange={(project) => handleProjectFilterChange(project, false)}
                   loading={loading}
               />
           )}
          
           {/* My Individual Collections Section - Show for all users */}
           <IndividualCollectionSection
               attributeName='My Individual Collections'
               count={myIndividualCount}
               isSelected={myIndividualSelected}
               onFilterChange={() => handleIndividualFilterChange('my')}
               loading={loading}
           />
          
           {/* Shared Individual Collections Section - Show only for Teams PRO users */}
           {isTeamsProUser && (
               <IndividualCollectionSection
                   attributeName='Shared Individual Collections'
                   count={sharedIndividualCount}
                   isSelected={sharedIndividualSelected}
                   onFilterChange={() => handleIndividualFilterChange('shared')}
                   loading={loading}
               />
           )}
       </>
   );
}


interface ProjectDropdownProps {
   attributeName: string;
   items: ProjectItem[];
   onFilterChange: (project: ProjectItem) => void;
   loading: boolean;
}


function ProjectDropdown({ attributeName, items, onFilterChange, loading }: ProjectDropdownProps) {
   const hasItemsWithCollections = items.some(item => item.count > 0);


   // Don't render if no projects have collections
   if (!hasItemsWithCollections) {
       return null;
   }


   return (
       <Grid container alignItems="center">   
           <Grid item xs={12}>
               <Typography style={{color:'black', marginBottom: '2px'}}>{attributeName}</Typography>
           </Grid>
          
           {/* All items in original order */}
           <Grid item xs={12} sx={{maxHeight:'calc(74.5vh - 270px)', overflowX:'clip', overflowY:'auto'}}>
               {items.filter(item => item.count > 0).map((item, index) => (
                   <Grid item xs={12} key={`item-${index}`} style={{height:'29px', whiteSpace: 'nowrap', overflow: 'hidden'}}>
                       <Checkbox
                           size='medium'
                           style={{padding: '7px'}}
                           checked={item.isRefined}
                           onChange={() => onFilterChange(item)}
                           disabled={loading}
                       />
                       <Typography variant='caption' style={{color:'black'}}>
                           {item.name} ({item.count})
                       </Typography>
                   </Grid>
               ))}
           </Grid>
          
           <Grid item xs={12}>
               <Divider style={{paddingTop:'9px'}} />
           </Grid>
       </Grid>
   );
}


interface IndividualCollectionSectionProps {
   attributeName: string;
   count: number;
   isSelected: boolean;
   onFilterChange: () => void;
   loading: boolean;
}


function IndividualCollectionSection({ attributeName, count, isSelected, onFilterChange, loading }: IndividualCollectionSectionProps) {
   // Don't render if no collections are present
   if (count === 0) {
       return null;
   }


   return (
       <Grid container alignItems="center">   
           <Grid item xs={12} style={{minHeight:'29px', display: 'flex', alignItems: 'flex-start'}}>
           <Checkbox
                   size='medium'
                   style={{padding:'7px', flexShrink: 0}}
                   checked={isSelected}
                   onChange={onFilterChange}
                   disabled={loading}
               />
           <Typography
                   style={{
                       color:'black',
                       cursor: 'pointer',
                       lineHeight: '1.2',
                       wordWrap: 'break-word',
                       overflowWrap: 'break-word',
                       marginTop: '9px'
                   }}
                   onClick={onFilterChange}
               >
                   {attributeName} ({count})
               </Typography>
             
           </Grid>
           <Grid item xs={12}>
               <Divider style={{paddingTop:'9px'}} />
           </Grid>
       </Grid>
   );
}

